import React, { useState } from "react";
import styled from "@emotion/styled";

import { InRowSaveToButton } from "components/opportunities/saving/InRowSaveToButton";
import BuyerCategoriesWithPopover from "components/table_components/BuyerCategoriesWithPopover";
import {
  EllipsisTooltipTextLink,
  EllipsisTooltipWouterLink,
} from "lib/core_components/EllipsisTooltip";
import { BuyerCategoryAssignmentDto } from "lib/generated/app-api";
import { useBuyersInLists } from "lib/hooks/api/buyer_lists/useBuyerLists";
import { EventData, TrackingProvider } from "lib/tracking";
import { OpportunityEntityType } from "lib/types/graphQLEnums";
import { getBuyerSupplierRelationshipDestination } from "../../lib/appDestinations";
import PaywallPopover from "../paywall/PaywallPopover";

export function NameColumn({
  buyerGuid,
  buyerName,
  categories,
  isSupplierRelationship,
  trackingData,
}: {
  buyerGuid: string;
  buyerName: string;
  categories: BuyerCategoryAssignmentDto[];
  isSupplierRelationship: boolean;
  trackingData?: EventData;
}) {
  const [mouseEntered, setMouseEntered] = useState(false);

  const {
    savedState: { isSaved, savedListNames },
  } = useBuyersInLists([buyerGuid]);

  return (
    <TrackingProvider data={{ "Buyer name": buyerName, ...trackingData }}>
      <PaywallPopover
        featureType={window.guestUser ? "GUEST_ACCESS_UPGRADE" : "BUYERS"}
        showModalOnClick={!!window.guestUser}
        contextSource="In-row"
        trackingData={trackingData}
        isDisabled={!window.guestUser}
      >
        <NameColumnContainer
          onMouseEnter={() => setMouseEntered(true)}
          onMouseLeave={() => setMouseEntered(false)}
        >
          <BuyerNameContainer onClick={(e) => e.stopPropagation()}>
            {isSupplierRelationship ? (
              <StyledEllipsisTooltipWouterLink
                fullText={buyerName}
                linkText={buyerName}
                linkProps={{
                  to: getBuyerSupplierRelationshipDestination(buyerGuid, "", "buyer"),
                  authorised: !window.guestUser,
                }}
              />
            ) : (
              <StyledEllipsisTooltipTextLink
                fullText={buyerName}
                linkText={buyerName}
                linkProps={{
                  to: `/buyers/${buyerGuid}`,
                  authorised: !window.guestUser,
                }}
              />
            )}
            <BuyerCategoriesWithPopover
              size="default"
              buyerCategories={categories.map((c) => ({
                buyer_category_id: c.buyerCategoryId,
                source: c.source,
              }))}
              buyerName={buyerName}
            />
          </BuyerNameContainer>
          <div
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
            }}
          >
            {!window.guestUser && (
              <InRowSaveToButton
                entityType="buyer"
                entityIds={[buyerGuid]}
                entityName={buyerName}
                displayType={mouseEntered ? "button" : "bookmark"}
                isSaved={isSaved}
                savedCount={savedListNames.length}
                defaultOpportunityValues={{
                  name: buyerName,
                  assignedToId: window.currentUser?.guid,
                  entities: [
                    {
                      entityId: buyerGuid,
                      entityType: OpportunityEntityType.Organisation,
                    },
                  ],
                }}
              />
            )}
          </div>
        </NameColumnContainer>
      </PaywallPopover>
    </TrackingProvider>
  );
}

const NameColumnContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr max-content;
  line-height: 32px;
`;

const BuyerNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledEllipsisTooltipWouterLink = styled(EllipsisTooltipWouterLink)`
  color: ${({ theme }) => theme.colors.sysPrimaryDefault};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.sysPrimaryHover};
  }
`;

const StyledEllipsisTooltipTextLink = styled(EllipsisTooltipTextLink)`
  color: ${({ theme }) => theme.colors.sysPrimaryDefault};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.sysPrimaryHover};
  }
`;
